/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { nanoid } from 'nanoid';
import {
  useDataProvider,
  Label,
  SubSection,
  SnackbarApiErrorMessage,
  useLocalization,
  useDialog,
  useSnackbar,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import IconLibrary from '../../../IconLibrary';
import DialogCloseButton from '../../../Dialogs/DialogCloseButton';
import ClaimParts from './ClaimParts';
import ReqAuthApprWidget from './ReqAuthApprWidget';
import ClaimDetail from './ClaimDetail';
import claimButtonsConfig from './claimButtonsConfig';
import RejectClaimModal from './submodals/RejectClaimModal';
import RejectOverrideClaimModal from './submodals/RejectOverrideClaimModal';
import ApproveOverrideClaimModal from './submodals/ApproveOverrideClaimModal';
import ClaimPaymentDetails from './ClaimPaymentDetails';
import Currency from '../../../Currency';

const defaultInitialValues = {
  parts: [],
  totalAmountApproved: '',
  totalAmountRequested: '',
  amounts: [],
};

const HIDE_APPROVED_FOR_CLAIMSTATUS = ['INCOMPLETE', 'CORRECTION_DRAFT'];

function ClaimDetailDialog({
  open,
  onClose,
  claimId = null,
  serviceJobId,
  serviceJobStatus,
  serviceJobFormData = {},
}) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, put, post, get } = useDataProvider(provider);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(defaultInitialValues);
  const [currentClaimData, setCurrentClaimData] = useState({});
  const [currentClaimPermissionData, setCurrentClaimPermissionData] = useState(
    {},
  );
  const [hasUpdate, setHasUpdate] = useState(null);
  const [currentClaimId, setCurrentClaimId] = useState(claimId);
  const [hideApproved, setHideApproved] = useState(true);

  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openRejectOverrideModal, setOpenRejectOverrideModal] = useState(false);
  const [openApproveOverrideModal, setOpenApproveOverrideModal] =
    useState(false);
  const { confirmationRef } = useDialog();
  const [claimCount, setClaimCount] = useState(0);

  const fetchClaims = async jobId => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/claims`,
    );
    if (response.ok) {
      setClaimCount(response?.data?.data?.length || 0);
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
    return null;
  };

  const schema = yup.object().shape({
    parts: yup
      .array()
      .of(
        yup.object({
          partNumber: yup.string().required('Required'),
          partUnitCost: yup.string().required('Required'),
          partQuantity: yup.string().required('Required'),
        }),
      )
      .required('Parts section has errors'),
  });

  const methods = useForm(
    {
      resolver: yupResolver(schema),
      defaultValues: initialValues,
    },
    [initialValues],
  );

  const formRef = useRef(methods);
  let { claimUiSchema } = serviceJobFormData || {};
  if (!claimUiSchema) {
    claimUiSchema = {};
  }
  const { partsAllowed, allowedAmountTypes, partUnusedFields } = {
    ...claimUiSchema,
  };

  const shouldHideApproved = currentClaimStatusArg => {
    return (
      !currentClaimStatusArg ||
      HIDE_APPROVED_FOR_CLAIMSTATUS.includes(currentClaimStatusArg)
    );
  };

  const fetchClaim = async () => {
    const url = `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/claims/${currentClaimId}`;
    const response = await get(url);
    if (response?.ok) {
      return response?.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const fetchClaimPermissions = async status => {
    let url = `${getApiUrl()}/sxcl/v1/permissions/config?entityName=CLAIM`;
    if (status) {
      url += `&entityStatus=${status}`;
    }

    const response = await get(url);
    if (response?.ok) {
      return response?.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const preProcessResponseData = apiData => {
    let amounts = apiData?.claimAmounts?.reduce((acc, a) => {
      return {
        ...acc,
        [a?.amountType.toUpperCase()]: {
          amountRequested: a?.amountRequested,
          amountAuthorized: a?.amountAuthorized,
          amountApproved: a?.amountApproved,
          previousApproved: a?.previousApproved,
          remainingAuthorized: a?.remainingAuthorized,
        },
      };
    }, {});

    amounts = {
      ...amounts,
      PARTS_TOTAL: {
        amountApproved: apiData?.totalPartsApproved,
        amountAuthorized: apiData?.totalPartsAuthorized,
        amountRequested: apiData?.totalPartsRequested,
        previousApproved: apiData?.totalPartsPreviousApproved || 0,
        remainingAuthorized: apiData?.totalPartsRemainingAuthorized || 0,
      },
    };

    const parts = apiData?.claimParts?.map(cp => {
      const partHandling =
        cp?.amounts?.find(cpa => cpa.amountType === 'HANDLING')
          ?.amountRequested || 0;
      const partTax =
        cp?.amounts?.find(cpa => cpa.amountType === 'TAX')?.amountRequested ||
        0;

      const {
        amounts: amountsArg,
        partDistributor,
        invoiceNumber,
        referenceNumber,
        totalRequested,
        totalApproved,
        quantity,
        partCost,
        ...rest
      } = cp;

      return {
        ...rest,
        partQuantity: quantity,
        partUnitCost: partCost,
        partHandling,
        partTax,
      };
    });

    return { amounts, parts };
  };

  const removePartsTotal = array => {
    return array?.filter(a => a.amountType !== 'PARTS_TOTAL');
  };

  const preProcessRequestData = (data, addTotalPartsApproved = false) => {
    if (!data) {
      return {};
    }
    const amountRequestedPermission =
      currentClaimPermissionData?.fields?.claimAmounts?.amountRequested;
    const amountApprovedPermission =
      currentClaimPermissionData?.fields?.claimAmounts?.amountApproved;

    const totalPartsApproved = data?.amounts?.PARTS_TOTAL?.amountApproved;

    let claimAmounts = Object?.entries(data?.amounts || {})?.reduce(
      (acc, [key, value]) => {
        let obj = {};

        if (amountRequestedPermission === 'WRITE') {
          obj = {
            amountRequested: value?.amountRequested || 0,
            amountType: key?.toUpperCase(),
          };
        }

        if (!hideApproved && amountApprovedPermission === 'WRITE') {
          obj = {
            ...obj,
            amountApproved: value?.amountApproved || 0,
            amountType: key?.toUpperCase(),
          };
        }

        if (Object.entries(obj).length > 0) {
          return [...acc, obj];
        }
        return acc;
      },
      [],
    );

    let claimParts = data?.parts?.map(p => {
      const part = {
        ...(p.id && { id: p.id }),
        partNumber: p?.partNumber,
        partDescription: p?.partDescription,
        partDistributor: p?.partDistributor,
        invoiceNumber: p?.invoiceNumber,
        referenceNumber: p?.referenceNumber,
        quantity: parseInt(p?.partQuantity, 10),
        partCost: parseFloat(p?.partUnitCost),
      };
      const partAmounts = [];
      if (
        currentClaimPermissionData?.fields?.claimParts?.amounts?.amountRequested
      ) {
        if (!partUnusedFields?.includes('HANDLING')) {
          partAmounts.push({
            amountRequested: p?.partHandling ? parseFloat(p?.partHandling) : 0,
            amountType: 'HANDLING',
          });
        }
        if (!partUnusedFields?.includes('TAX')) {
          partAmounts.push({
            amountRequested: p?.partTax ? parseFloat(p?.partTax) : 0,
            amountType: 'TAX',
          });
        }
        part.amounts = partAmounts;
      }
      return part;
    });

    // If currently parts are deleted and the claim had parts originally
    if (claimParts?.length === 0) {
      if (
        Array.isArray(initialValues?.parts) &&
        initialValues?.parts?.length > 0
      ) {
        claimParts = [];
      } else {
        claimParts = null;
      }
    }

    // If currently parts are deleted and the claim had parts originally
    if (claimAmounts?.length === 0) {
      const withoutPartsValues = removePartsTotal(initialValues?.amounts);
      if (
        Array.isArray(withoutPartsValues?.amounts) &&
        withoutPartsValues?.amounts?.length > 0
      ) {
        claimAmounts = [];
      } else {
        claimAmounts = null;
      }
    }

    const body = {
      recordVersion: currentClaimData?.recordVersion,
      ...(claimAmounts && {
        claimAmounts: removePartsTotal(claimAmounts),
      }),
      ...(claimParts && { claimParts }),
      ...(addTotalPartsApproved && { totalPartsApproved }),
    };
    return body;
  };

  const initialize = async () => {
    setLoading(true);
    if (currentClaimId) {
      const claimData = await fetchClaim();
      setCurrentClaimData(claimData);
      const claimPermissions = await fetchClaimPermissions(
        claimData?.claimStatus,
      );
      setCurrentClaimPermissionData(claimPermissions);

      setHideApproved(shouldHideApproved(claimData?.claimStatus));
      const formattedData = preProcessResponseData(claimData);
      const initialValuesArg = { ...defaultInitialValues, ...formattedData };
      setInitialValues(initialValuesArg);
    } else {
      const claimPermissions = await fetchClaimPermissions();
      setCurrentClaimPermissionData(claimPermissions);
    }
    await fetchClaims(serviceJobId);
    setLoading(false);
  };

  const refreshClaimDialog = response => {
    const formattedData = preProcessResponseData(response?.data);
    methods.reset(formattedData);
    setCurrentClaimId(response?.data?.id);
    setCurrentClaimData(response?.data);
    setHideApproved(shouldHideApproved(response?.data?.claimStatus));
  };

  const onFormSubmit = async (data, button) => {
    const submitter = button?.nativeEvent?.submitter;
    const action = submitter?.value || submitter?.dataset?.action;
    setLoading(true);

    if (action === 'APPROVE_OVERRIDE') {
      setOpenApproveOverrideModal(true);
    } else if (action === 'REJECT_OVERRIDE') {
      setOpenRejectOverrideModal(true);
    } else if (action === 'REJECT') {
      setOpenRejectModal(true);
    } else if (action === 'CANCEL') {
      confirmationRef.current.init({
        message: 'Do you want to cancel this claim',
        positiveButtonText: 'Yes',
        negativeButtonText: 'No',
        width: 360,
        title: 'Cancel Claim',
        onChange: async yes => {
          if (yes) {
            const url = `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/claims/${currentClaimId}/cancel?recordVersion=${
              currentClaimData?.recordVersion
            }`;
            const response = await put(url);
            if (response?.ok && response.data) {
              snackbar.showMessage(
                formatMessage({
                  id: 'CLAIM_CANCEL_SUCCESS',
                  defaultMessage: 'Claim was cancelled successfully',
                }),
              );
              confirmationRef.current.setOpen(false);
              onClose(true);
            } else {
              snackbar.showMessage({
                type: 'error',
                data: <SnackbarApiErrorMessage errors={response?.errors} />,
              });
              confirmationRef.current.setOpen(false);
            }
          } else {
            confirmationRef.current.setOpen(false);
          }
          return null;
        },
      });
      confirmationRef.current.setOpen(true);
    } else if (action === 'APPROVE') {
      const body = preProcessRequestData(data, true);
      const partsAmount = parseFloat(body?.totalPartsApproved || 0) || 0;
      const totalApprovedAmount =
        (body?.claimAmounts.reduce(
          (acc, item) => acc + (parseFloat(item?.amountApproved || 0) || 0),
          0,
        ) || 0) + partsAmount;

      const hasParts = Array.isArray(data?.parts) && data?.parts?.length > 0;

      const noPartsButPartAmountExists = !hasParts && partsAmount > 0;

      const message = (
        <Box>
          <Typography>
            Do you want to approve this claim for{' '}
            <Currency>{totalApprovedAmount}</Currency>?
          </Typography>
          {noPartsButPartAmountExists && (
            <Typography sx={{ mt: 1 }} variant="subtitle2">
              This Claim has Parts Approved with no Parts requested.
            </Typography>
          )}
        </Box>
      );
      confirmationRef.current.init({
        message,
        positiveButtonText: 'Yes',
        negativeButtonText: 'No',
        width: 360,
        title: 'Approve Claim',
        onChange: async yes => {
          if (yes) {
            const url = `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/claims/${currentClaimId}/approve`;
            const response = await put(url, {
              claimAmounts: body?.claimAmounts.map(c => ({
                amountApproved: c?.amountApproved,
                amountType: c?.amountType,
              })),
              totalPartsApproved: body?.totalPartsApproved,
              recordVersion: currentClaimData?.recordVersion,
            });

            if (response?.ok) {
              snackbar.showMessage(
                formatMessage({
                  id: 'CLAIM_APPROVE_SUCCESS',
                  defaultMessage: 'Claim was approved successfully',
                }),
              );
              confirmationRef.current.setOpen(false);
              onClose(true);
            } else if (!response?.ok && response?.statusCodeelse === 422) {
              snackbar.showMessage({
                type: 'error',
                data: <SnackbarApiErrorMessage errors={response?.errors} />,
              });
              refreshClaimDialog(response);
              confirmationRef.current.setOpen(false);
            } else {
              snackbar.showMessage({
                type: 'error',
                data: <SnackbarApiErrorMessage errors={response?.errors} />,
              });
              confirmationRef.current.setOpen(false);
            }
          } else {
            confirmationRef.current.setOpen(false);
          }
          return null;
        },
      });
      confirmationRef.current.setOpen(true);
    } else if (action === 'SUBMIT') {
      confirmationRef.current.init({
        message: 'Do you want to submit this claim',
        positiveButtonText: 'Yes',
        negativeButtonText: 'No',
        width: 360,
        title: 'Submit Claim',
        onChange: async yes => {
          if (yes) {
            let url = `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/claims/submit`;
            if (currentClaimId) {
              url = `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/claims/${currentClaimId}/submit`;
            }
            const body = preProcessRequestData(data);

            const response = await put(url, body);
            if (response?.ok && response.data) {
              snackbar.showMessage(
                formatMessage({
                  id: 'CLAIM_SUBMIT_SUCCESS',
                  defaultMessage: 'Claim was submitted successfully',
                }),
              );
              confirmationRef.current.setOpen(false);
              onClose(true);
            } else {
              snackbar.showMessage({
                type: 'error',
                data: <SnackbarApiErrorMessage errors={response?.errors} />,
              });
              confirmationRef.current.setOpen(false);
            }
          } else {
            confirmationRef.current.setOpen(false);
          }
          return null;
        },
      });
      confirmationRef.current.setOpen(true);
    } else if (action === 'REOPEN') {
      confirmationRef.current.init({
        message: 'Do you want to reopen this claim',
        positiveButtonText: 'Yes',
        negativeButtonText: 'No',
        width: 360,
        title: 'Reopen Claim',
        onChange: async yes => {
          if (yes) {
            const url = `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/claims/${currentClaimId}/reopen?recordVersion=${
              currentClaimData?.recordVersion
            }`;

            const response = await put(url);
            if (response?.ok && response.data) {
              snackbar.showMessage(
                formatMessage({
                  id: 'CLAIM_REOPEN_SUCCESS',
                  defaultMessage: 'Claim was reopened successfully',
                }),
              );
              confirmationRef.current.setOpen(false);
              onClose(true);
            } else {
              snackbar.showMessage({
                type: 'error',
                data: <SnackbarApiErrorMessage errors={response?.errors} />,
              });
              confirmationRef.current.setOpen(false);
            }
          } else {
            confirmationRef.current.setOpen(false);
          }
          return null;
        },
      });
      confirmationRef.current.setOpen(true);
    } else if (action === 'UPDATE' || action === 'CREATE') {
      let url = `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/claims`;
      let method = post;
      const methodData = data;
      switch (action) {
        case 'UPDATE': {
          url = `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/claims/${currentClaimId}`;
          method = put;
          break;
        }
        default:
      }

      const body = preProcessRequestData(methodData);
      const response = await method(url, body);
      if (response.ok) {
        snackbar.showMessage(
          formatMessage({
            id: 'CLAIM_SAVE_SUCCESS',
            defaultMessage: 'Claim was saved successfully',
          }),
        );
        refreshClaimDialog(response);
        setHasUpdate(nanoid());
      } else {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={response?.errors} />,
        });
      }
    }
    setLoading(false);
  };

  const createTitle = formatMessage({
    id: 'CREATE_CLAIM',
    defaultMessage: 'Create Claim',
  });

  const editTitle = formatMessage({
    id: 'EDIT_CLAIM',
    defaultMessage: 'Edit Claim',
  });

  const closeButton = {
    children: formatMessage({ id: 'CLOSE', defaultMessage: 'Close' }),
    onClick: () => onClose(hasUpdate),
    variant: 'outlined',
    color: 'tertiary',
    value: 'CLOSE',
    align: 'right',
  };

  useEffect(() => {
    if (open) {
      initialize();
    }
  }, [currentClaimId, JSON.stringify(currentClaimData)]);

  useEffect(() => {
    methods.reset(initialValues);
  }, [initialValues, currentClaimId]);

  const finalButtons = claimButtonsConfig(formatMessage).filter(cbc => {
    if (cbc.type === 'divider') {
      return true;
    }
    if (currentClaimPermissionData?.actions?.includes(cbc.value)) {
      return true;
    }
    return false;
  });
  finalButtons.push(closeButton);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.Claim />
        {currentClaimId === null ? createTitle : editTitle}
        <DialogCloseButton disabled={loading} onClose={onClose} />
      </DialogTitle>
      <FormProvider {...methods} context="ApproveOverride">
        <form onSubmit={methods.handleSubmit(onFormSubmit)}>
          <DialogContent>
            {loading ? (
              <Box textAlign="center">
                <CircularProgress size="30px" />
                <Typography>Processing</Typography>
              </Box>
            ) : (
              <Grid2 container spacing={1}>
                {currentClaimId && (
                  <Grid2 xs={12} sx={{ mt: 1 }}>
                    <ClaimDetail claimData={currentClaimData} />
                  </Grid2>
                )}
                {partsAllowed && (
                  <Grid2 xs={12} sx={{ mt: 1 }}>
                    <Box>
                      <Label>Parts</Label>
                    </Box>
                    <SubSection>
                      <Controller
                        control={methods.control}
                        name="parts"
                        render={({ field }) => {
                          return (
                            <ClaimParts
                              permissions={currentClaimPermissionData}
                              field={field}
                              unusedFields={partUnusedFields}
                            />
                          );
                        }}
                      />
                    </SubSection>
                  </Grid2>
                )}

                <Grid2 xs={12}>
                  <SubSection sx={{ mt: 3 }}>
                    <Controller
                      control={methods.control}
                      name="amounts"
                      render={({ field }) => {
                        return (
                          <ReqAuthApprWidget
                            {...field}
                            claimId={currentClaimId}
                            permissions={currentClaimPermissionData}
                            claimData={currentClaimData}
                            allowedAmountTypes={allowedAmountTypes}
                            partsAllowed={partsAllowed}
                            claimCount={claimCount}
                          />
                        );
                      }}
                    />
                  </SubSection>
                </Grid2>
                {currentClaimPermissionData?.fields?.claimPayment && (
                  <Grid2 xs={12} sx={{ mt: 1 }}>
                    <Box>
                      <Label>Payment Details</Label>
                    </Box>
                    <SubSection>
                      <Controller
                        control={methods.control}
                        name="paymentDetails"
                        render={() => {
                          return (
                            <ClaimPaymentDetails
                              serviceJobId={serviceJobId}
                              claimId={currentClaimId}
                            />
                          );
                        }}
                      />
                    </SubSection>
                  </Grid2>
                )}
              </Grid2>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            {finalButtons.map((btn, idx) => {
              const { children, claimStatuses, ...rest } = btn;
              if (btn.type === 'divider') {
                return (
                  <Box key={`divider${idx}`} sx={{ width: '50px' }}>
                    {}
                  </Box>
                );
              }
              return (
                <Button
                  sx={{ ml: 1 }}
                  key={children}
                  disabled={loading}
                  {...rest}>
                  {children}
                </Button>
              );
            })}
          </DialogActions>
        </form>
      </FormProvider>
      {openRejectModal && (
        <RejectClaimModal
          open={openRejectModal}
          onClose={() => {
            setOpenRejectModal(false);
            onClose(true);
          }}
          setOpenModal={setOpenRejectModal}
          claimId={currentClaimId}
          serviceJobId={serviceJobId}
          formRef={formRef}
          preProcessClaimRequest={preProcessRequestData}
        />
      )}
      {openRejectOverrideModal && (
        <RejectOverrideClaimModal
          open={openRejectOverrideModal}
          onClose={() => {
            setOpenRejectOverrideModal(false);
            onClose(true);
          }}
          setOpenModal={setOpenRejectOverrideModal}
          claimId={currentClaimId}
          serviceJobId={serviceJobId}
          formRef={formRef}
          preProcessClaimRequest={preProcessRequestData}
        />
      )}
      {openApproveOverrideModal && (
        <ApproveOverrideClaimModal
          open={openApproveOverrideModal}
          onClose={() => {
            setOpenApproveOverrideModal(false);
            onClose(true);
          }}
          claimId={currentClaimId}
          serviceJobId={serviceJobId}
          formRef={formRef}
          setOpenModal={setOpenApproveOverrideModal}
          preProcessClaimRequest={preProcessRequestData}
          serviceJobStatus={serviceJobStatus}
        />
      )}
    </Dialog>
  );
}

export default ClaimDetailDialog;
